import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  BarChart as ReBarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Grid, makeStyles, Typography } from "@material-ui/core";

import { InfoBox } from "../../../../components/boxes/InfoBox";
import { COLORS } from "../../../../theme/colors";
import { Box } from "../Box";
import { GroupButton } from "../GroupButton";
import { Header } from "../Header";
import { expectedCarbonRevernuesPotential, expectedEmissionReductionPotential } from "../sample";
import { CarboonRevenueLabel, EmissionReductionLabel } from "./labels";
import { Tooltip } from "../../../../components/charts/Tooltip";
import { TooltipContent } from "../../../../components/charts/TooltipContent";

const useStyles = makeStyles((theme) => ({
  chartTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
}));

const XAxis = () => (
  <Grid container>
    <Grid item xs={6}>
      <Typography align="center">Portfolio avg.</Typography>
    </Grid>
    <Grid item xs={6}>
      <Typography align="center">This building with new power asset</Typography>
    </Grid>
  </Grid>
);

const HighestCarbonReductionPotentialBarChart = ({
  chartData,
  barColors,
  customLabelList,
  additionalBars,
  unit,
}) => {
  const BAR_SIZE = 20;

  useEffect(() => {
    if (!additionalBars) {
      return;
    }
    setTimeout(() => {
      for (let i = 0; i < additionalBars.length; i++) {
        const className = additionalBars[i].key;
        const tags = document.getElementsByClassName(`recharts-rectangle ${className}`);
        if (tags.length) {
          for (const tag of tags) {
            const d = tag.getAttribute("d");
            const x = parseInt(d.split(" ")[1].split(",")[0]);
            tag.setAttribute("d", `M ${x + BAR_SIZE * (i + 1)},${d.split(",")[1]}`);
          }
        }
      }
    }, 800);
  }, [additionalBars]);

  return (
    <ResponsiveContainer width="100%" height={170}>
      <ReBarChart
        data={chartData}
        margin={{
          top: 20,
          bottom: 5,
        }}
        barSize={BAR_SIZE}
      >
        <CartesianGrid vertical={false} strokeDasharray="5 2" />
        <Tooltip chartData={chartData} content={<TooltipContent />} />
        <Bar
          dataKey="trading"
          stackId="a"
          fill={barColors && barColors[0] ? barColors[0] : COLORS.purple}
          unit={unit}
        />
        <Bar
          dataKey="battery"
          stackId="a"
          fill={barColors && barColors[1] ? barColors[1] : "#4F31CF"}
          unit={unit}
        />
        <Bar
          dataKey="ev"
          stackId="a"
          fill={barColors && barColors[2] ? barColors[2] : COLORS.cian}
          unit={unit}
        />
        {additionalBars &&
          additionalBars.map((additionalBar, index) => (
            <Bar
              key={index}
              dataKey={additionalBar.key}
              stackId="a"
              fill={additionalBar.color}
              className={additionalBar.key}
              unit={unit}
            >
              {index === additionalBars.length - 1 && (
                <LabelList dataKey="total" content={customLabelList} position="top" offset={10} />
              )}
            </Bar>
          ))}
      </ReBarChart>
    </ResponsiveContainer>
  );
};

export const HighestCarbonReductionPotential = ({ building, onClose }) => {
  const classes = useStyles();
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  return (
    <Box>
      <Header
        title="Highest Carbon Emission Reduction Potential"
        hideTenants
        building={building}
        onClose={onClose}
      />

      <InfoBox color="#E7FCDC">
        <Typography variant="h4">
          High potential to increase this building&apos;s carbon reduction and revenues.
          <Typography variant="h3" style={{ display: "inline" }}>
            Your investment: {currencySymbol}0
          </Typography>
        </Typography>
      </InfoBox>

      <Grid container spacing={4}>
        <Grid item xs={6}>
          <Typography className={classes.chartTitle}>Expected Carbon Emission Reduction</Typography>
          <HighestCarbonReductionPotentialBarChart
            chartData={expectedEmissionReductionPotential}
            additionalBars={[{ key: "additional_emission", color: "#D18731" }]}
            customLabelList={EmissionReductionLabel}
            unit="KgCo2"
          />
          <XAxis />
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.chartTitle}>Expected Carbon Revenues</Typography>
          <HighestCarbonReductionPotentialBarChart
            chartData={expectedCarbonRevernuesPotential}
            barColors={["#019275", "#97FF9F", "#27E7C1"]}
            additionalBars={[
              { key: "additional_carbon_rev", color: COLORS.silverSand[50] },
              { key: "additional_pwr_rev", color: "#EDC124" },
            ]}
            customLabelList={CarboonRevenueLabel}
          />
          <XAxis />
        </Grid>
      </Grid>

      <GroupButton
        buttons={[{ text: "SHARE" }, { text: "APPROVE NEW POWER ASSET" }]}
        additionalEl={
          <Typography style={{ fontWeight: 600, marginLeft: 4 }}>
            (COST: {currencySymbol}0)
          </Typography>
        }
      />
    </Box>
  );
};
